/**
 * Service hours for one site: the resolved week, where it came from, why a stored value was
 * rejected, and an editor (POS2-1210).
 *
 * Site-level only, and its own component rather than a sixth field in `ConfigOverrides`, because
 * the other five are shown on four entity types and this is shown on one. `resolve_for_site` walks
 * site → account → organisation and never a kitchen, so a kitchen-level value would have no reader
 * at all; account level does have one, and is left out so there is one place to set service hours
 * instead of three to reconcile.
 *
 * **The display stays general even though the editor does not.** The source line names whichever
 * level supplied the resolved value, so a value written some other way is visible here rather than
 * silently governing the report's capture-gap threshold.
 *
 * `invalid_at` is the second thing this screen exists for. A stored value the resolver rejected is
 * skipped, the walk falls through to the platform default, and the report prints its weakened line
 * rather than telling a customer their configuration is broken — which leaves a log entry and a
 * Sentry warning as the only trace. This is the screen that can fix it, so this is where it says so.
 */

import { AlertTriangle, Plus, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useResolvedConfig, type ConfigLevel } from "@/hooks/use-resolved-config";
import { useTranslation } from "@/i18n/use-translation";
import { weekdayLabels } from "@/lib/format";
import {
  WEEKDAYS,
  addedWindow,
  dayProblems,
  parseWeek,
  summariseWeek,
  type DayProblem,
  type OperatingHours,
  type Weekday,
} from "@/lib/operating-hours";

/** Literal keys in a lookup table, which is the one indirection `check:i18n` can still resolve. */
const PROBLEM_KEYS: Record<DayProblem, string> = {
  incomplete: "settings.serviceHoursIncomplete",
  closesBeforeOpens: "settings.serviceHoursClosesBeforeOpens",
  overlap: "settings.serviceHoursOverlap",
};

const LEVEL_KEYS: Record<ConfigLevel, string> = {
  organisation: "settings.organisationTitle",
  account: "settings.account",
  site: "settings.site",
  kitchen: "settings.kitchen",
};

/** `source` is a level or the platform. Named rather than passed through raw: the five fields above
 * this one interpolate the API's own English word into a German sentence, which is a gap worth its
 * own ticket and not a convention to copy. */
const SOURCE_KEYS: Record<ConfigLevel | "platform", string> = {
  ...LEVEL_KEYS,
  platform: "settings.platform",
};

interface ServiceHoursFieldProps {
  siteId: string | null;
  /** The site's parent, resolved separately — see the two-query note below. */
  accountId: string | null;
  /** The site's own override, or `null` for inherited. Never `{}` — see `lib/operating-hours.ts`. */
  value: OperatingHours | null;
  onChange: (value: OperatingHours | null) => void;
}

export function ServiceHoursField({ siteId, accountId, value, onChange }: ServiceHoursFieldProps) {
  const { t, locale } = useTranslation();
  // Two resolutions, because the screen asks two different questions and one endpoint answers only
  // the first. The site's own resolution describes what is *stored*, so on a site that overrides
  // service hours it reports `source: "site"` and the site's own week — which is the right answer
  // for `invalid_at` and the wrong one for "what happens if I stop overriding". Reading the
  // inherited display off it echoed the value being cleared straight back, labelled as inherited
  // from the site itself, which cannot be true of any site. The account walks
  // account → organisation → platform: exactly what applies when the site holds nothing.
  const { data: own, isLoading: ownLoading } = useResolvedConfig("site", siteId);
  const { data: parent, isLoading: parentLoading } = useResolvedConfig("account", accountId);
  const isLoading = ownLoading || parentLoading;

  if (isLoading) {
    // Same row shape as the loaded state, for the reason `ConfigOverrides` gives: this block sits
    // at the bottom of a dialog whose submit button is pinned, and growing on resolve moves it.
    return (
      <div>
        <p className="text-sm font-medium">{t("settings.serviceHours")}</p>
        <Skeleton className="mt-1.5 h-9 w-full rounded" />
        <Skeleton className="mt-1 h-4 w-2/3 rounded" />
      </div>
    );
  }

  // `invalid_at` comes from the site's own walk: it is the only one that can name the site level.
  const invalidAt = own?.config.operating_hours.invalid_at;
  const inheritedField = parent?.config.operating_hours;
  const inheritedWeek = parseWeek(inheritedField?.value);
  const problems = value ? dayProblems(value) : {};

  return (
    <div>
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium">{t("settings.serviceHours")}</p>
        {value ? (
          <button
            type="button"
            className="text-xs text-muted-foreground underline-offset-2 hover:underline"
            onClick={() => onChange(null)}
          >
            {t("settings.serviceHoursUseInherited")}
          </button>
        ) : null}
      </div>

      {value ? (
        <WeekEditor
          value={value}
          inherited={inheritedWeek}
          problems={problems}
          onChange={onChange}
        />
      ) : (
        <div className="mt-1.5">
          {inheritedWeek ? (
            <p className="text-sm">{summariseWeek(inheritedWeek, locale, t)}</p>
          ) : null}
          {inheritedField?.source ? (
            <p className="mt-1 text-xs text-muted-foreground">
              {t("settings.serviceHoursInheritedFrom", {
                source: t(SOURCE_KEYS[inheritedField.source]),
                name: inheritedField.source_name,
              })}
            </p>
          ) : null}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-2"
            // Seeded from the inherited week rather than from blank, so "set these hours" starts
            // from the hours that would otherwise apply and the edit is the difference from them.
            disabled={!inheritedWeek}
            onClick={() => inheritedWeek && onChange(inheritedWeek)}
          >
            {t("settings.serviceHoursSet")}
          </Button>
        </div>
      )}

      {invalidAt ? (
        <p className="mt-2 flex gap-1.5 text-xs text-destructive">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
          <span>{t("settings.serviceHoursInvalid", { level: t(LEVEL_KEYS[invalidAt]) })}</span>
        </p>
      ) : null}
    </div>
  );
}

function WeekEditor({
  value,
  inherited,
  problems,
  onChange,
}: {
  value: OperatingHours;
  /** The week that would apply if this site had no override — where a re-opened day gets its
   * shape from when the editor's own week has nothing to copy. */
  inherited: OperatingHours | null;
  problems: Partial<Record<Weekday, DayProblem>>;
  onChange: (value: OperatingHours) => void;
}) {
  const { t, locale } = useTranslation();
  const labels = weekdayLabels(locale);

  const setDay = (day: Weekday, windows: OperatingHours[Weekday]) =>
    onChange({ ...value, [day]: windows });

  return (
    <div className="mt-1.5 space-y-1.5 rounded-md border p-2">
      {WEEKDAYS.map((day, i) => {
        const problem = problems[day];
        return (
          <div key={day}>
            <div className="flex items-start gap-2">
              <span className="mt-1.5 w-9 shrink-0 text-xs font-medium">{labels[i]}</span>
              <div className="flex-1 space-y-1">
                {value[day].length === 0 ? (
                  <p className="mt-1.5 text-xs text-muted-foreground">
                    {t("settings.serviceHoursClosed")}
                  </p>
                ) : null}
                {value[day].map((window, index) => (
                  <div key={index} className="flex items-center gap-1">
                    <input
                      type="time"
                      aria-label={t("settings.serviceHoursOpensAt", { day: labels[i] })}
                      className="h-8 rounded-md border border-input bg-transparent px-2 text-xs"
                      value={window.opens_at}
                      onChange={(e) =>
                        setDay(
                          day,
                          value[day].map((w, j) =>
                            j === index ? { ...w, opens_at: e.target.value } : w,
                          ),
                        )
                      }
                    />
                    <span className="text-xs text-muted-foreground">–</span>
                    <input
                      type="time"
                      aria-label={t("settings.serviceHoursClosesAt", { day: labels[i] })}
                      className="h-8 rounded-md border border-input bg-transparent px-2 text-xs"
                      value={window.closes_at}
                      onChange={(e) =>
                        setDay(
                          day,
                          value[day].map((w, j) =>
                            j === index ? { ...w, closes_at: e.target.value } : w,
                          ),
                        )
                      }
                    />
                    <button
                      type="button"
                      aria-label={t("settings.serviceHoursRemoveWindow", { day: labels[i] })}
                      className="rounded p-1 text-muted-foreground hover:text-foreground"
                      onClick={() =>
                        setDay(
                          day,
                          value[day].filter((_, j) => j !== index),
                        )
                      }
                    >
                      <X className="size-3.5" />
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                aria-label={t("settings.serviceHoursAddWindow", { day: labels[i] })}
                className="mt-1 rounded p-1 text-muted-foreground hover:text-foreground"
                onClick={() => setDay(day, [...value[day], addedWindow(value, day, inherited)])}
              >
                <Plus className="size-3.5" />
              </button>
            </div>
            {problem ? (
              <p className="ml-11 text-xs text-destructive">{t(PROBLEM_KEYS[problem])}</p>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
